// src/components/Dashboard/ProjectHeader.tsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { Camera, Folder, MoreVertical, X } from 'lucide-react';
import type { ProjectInfo } from '../../types/dashboard';
import { dashboardService } from '@/services/dashboardService';
import { apiService } from '@/services/api';
import { authStorage } from '../../lib/storage';
import { config } from '../../config/env';
import { conversationBus } from '@/lib/conversationBus';
import { addTargetBlankToLinks } from '@/lib/linkUtils';
import ConversationApi from '../ConversationItem/api/ConversationApi';
import SummernoteCorgee from '../Globals/SummernoteCorgee';
import EditCard from '../EditCard/EditCard';
import type { EditCardRef } from '../EditCard/EditCard';
import '../../assets/fonts/glyphs/material-icons.css';
import '../../assets/fonts/glyphs/glyphs.css';
import '../../assets/fonts/glyphs-news/glyphs2.css';
import './ProjectHeader.css';
import { RenderInBody } from '../Globals';
import { ConfigSpModal, EditDashboardColumn } from './TabsNavigation/modals';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';

interface ProjectHeaderProps {
  projectInfo: ProjectInfo | null;
  projectId: number;
  onOpenInfoWindow: (conversationId: number) => void;
  onShowMoreDetails?: () => void;
}

export const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  projectInfo,
  projectId,
  onOpenInfoWindow,
  onShowMoreDetails,
}) =>
{
  const socketConnectionId = useSocketConnectionId();
  // --- Name editing ---
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(projectInfo?.Name || '');
  const [localName, setLocalName] = useState(projectInfo?.Name || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // --- Image upload ---
  const [imageHovered, setImageHovered] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Body modal (popover) ---
  const [showBodyModal, setShowBodyModal] = useState(false);
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  const [isBodyOverflowing, setIsBodyOverflowing] = useState(false);
  const bodyContentRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [draftBody, setDraftBody] = useState('');
  const summernoteRef = useRef<any>(null);
  const [savingBody, setSavingBody] = useState(false);
  const [modalAnchorPoint, setModalAnchorPoint] = useState<{ x: number; y: number } | null>(null);
  const [modalPopoverStyle, setModalPopoverStyle] = useState<{ top: number; left: number; arrowTop: number; ready: boolean }>({
    top: 0,
    left: 0,
    arrowTop: 24,
    ready: false,
  });
  const [localDescription, setLocalDescription] = useState(projectInfo?.Description || '');

  // --- Custom fields (inline preview only, full view via Details tab) ---

  // --- Dropdown menu ---
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // --- Show Edit Project Column
  const [configProjectColumn, setConfigProjectColumn] = useState<boolean>(false);

  // --- Show Edit Project Column
  const [configSpByPerson, setConfigSpByPerson] = useState<boolean>(false);

  // --- Edit Card ---
  const editCardRef = useRef<EditCardRef>(null);
  const editCardInitRef = useRef<any>(null);

  // --- API config for SummernoteCorgee ---
  const apiConfig = useMemo(() =>
  {
    const token = authStorage.getAccessToken() || '';
    // Resolved base URL already includes protocol and tenant subdomain
    let apiUrl = config.api.baseUrl;

    // callFetch concatenates url + endpoint directly
    if (!apiUrl.endsWith('/'))
    {
      apiUrl += '/';
    }

    return { url: apiUrl, token };
  }, []);

  // Listen for image updates from other components (Details tab, InfoWindow)
  useEffect(() =>
  {
    if (!projectInfo?.ConversationId) return;
    return conversationBus.on('image-updated', data =>
    {
      if (data.conversationId === projectInfo.ConversationId)
      {
        setLocalImageUrl(data.imageUrl);
      }
    });
  }, [projectInfo?.ConversationId]);

  // Listen for title updates from InfoWindow or other components
  useEffect(() =>
  {
    if (!projectInfo?.ConversationId) return;
    return conversationBus.on('title-updated', data =>
    {
      if (data.conversationId === projectInfo.ConversationId)
      {
        setLocalName(data.title);
        setDraftName(data.title);
      }
    });
  }, [projectInfo?.ConversationId]);

  // Listen for description/body updates from other components
  useEffect(() =>
  {
    if (!projectInfo?.ConversationId) return;
    return conversationBus.on('description-updated', data =>
    {
      if (data.conversationId === projectInfo.ConversationId)
      {
        setLocalDescription(data.description);
      }
    });
  }, [projectInfo?.ConversationId]);

  // Sync draft name when projectInfo changes
  useEffect(() =>
  {
    if (projectInfo?.Name)
    {
      setDraftName(projectInfo.Name);
      setLocalName(projectInfo.Name);
    }
  }, [projectInfo?.Name]);

  // Sync local description when projectInfo changes
  useEffect(() =>
  {
    setLocalDescription(projectInfo?.Description || '');
  }, [projectInfo?.Description]);

  // Detect whether the (collapsed) description overflows its clamped space,
  // so the See More/See Less toggle only shows up when it's actually needed
  useEffect(() =>
  {
    if (isBodyExpanded) return;

    const checkOverflow = () =>
    {
      const el = bodyContentRef.current;
      if (!el) return;
      setIsBodyOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [localDescription, isBodyExpanded]);

  // Keep the body-editor popover positioned next to the click point, using its
  // actual rendered height (Summernote's toolbar/content can load asynchronously)
  useEffect(() =>
  {
    if (!showBodyModal || !modalAnchorPoint) return;
    const el = popoverRef.current;
    if (!el) return;

    const reposition = () =>
    {
      const height = el.getBoundingClientRect().height;
      const left = Math.min(
        modalAnchorPoint.x + POPOVER_GAP,
        window.innerWidth - POPOVER_WIDTH - POPOVER_MARGIN,
      );
      const top = Math.min(
        Math.max(modalAnchorPoint.y - 24, POPOVER_MARGIN),
        window.innerHeight - height - POPOVER_MARGIN,
      );
      const arrowTop = Math.min(
        Math.max(modalAnchorPoint.y - top, 20),
        height - 20,
      );
      setModalPopoverStyle({ top, left, arrowTop, ready: true });
    };

    reposition();
    const resizeObserver = new ResizeObserver(reposition);
    resizeObserver.observe(el);
    window.addEventListener('resize', reposition);
    return () =>
    {
      resizeObserver.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, [showBodyModal, modalAnchorPoint]);

  // Focus input when editing
  useEffect(() =>
  {
    if (isEditing)
    {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  // Close dropdown on outside click
  useEffect(() =>
  {
    const handleClickOutside = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setMenuOpen(false);
      }
    };
    if (menuOpen)
    {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close the body-editor popover on Escape
  useEffect(() =>
  {
    if (!showBodyModal) return;

    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape')
      {
        setShowBodyModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showBodyModal]);

  // Early return if projectInfo is null
  if (!projectInfo)
  {
    return (
      <div className="project-header">
        <div className="project-header__skeleton">
          <div className="project-header__skeleton-image" />
          <div className="project-header__skeleton-content">
            <div className="project-header__skeleton-title" />
            <div className="project-header__skeleton-body" />
          </div>
        </div>
      </div>
    );
  }

  // ─── Handlers ─────────────────────────────────────────

  const saveProjectName = async () =>
  {
    const trimmedName = draftName.trim();
    if (!trimmedName || trimmedName === localName)
    {
      setDraftName(localName);
      setIsEditing(false);
      return;
    }
    try
    {
      await dashboardService.updateconversationtitle(
        trimmedName,
        projectInfo.ConfigValueID,
        projectInfo.ConversationId,
        socketConnectionId || '',
      );
      setLocalName(trimmedName);
      conversationBus.emit('title-updated', {
        conversationId: projectInfo.ConversationId,
        title: trimmedName,
      });
    }
    catch (err)
    {
      console.error('Error saving project name:', err);
      setDraftName(localName);
    }
    setIsEditing(false);
  };

  const cancelEdit = () =>
  {
    setDraftName(localName);
    setIsEditing(false);
  };

  // Image upload
  const handleImageClick = () =>
  {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const file = e.target.files?.[0];
    if (!file) return;

    const layerZero = projectInfo.LayerCustomFields?.find(layer => layer.LayerId === 0);
    const photoField = layerZero?.CustomFields?.find((field: any) => field.Description === 'Photo');
    const customFieldValueId = photoField?.CustomFieldValueId || 0;

    // Optimistic: show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setLocalImageUrl(localUrl);

    try
    {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('customFieldValueId', String(customFieldValueId));

      const response = await dashboardService.uploadProjectImage(formData);

      const serverUrl = response?.ThumbnailPath || localUrl;

      // When customFieldValueId is 0, the upload creates the file but doesn't link it
      // to the project's custom field. We need to call POST /customfieldvalue/ to persist it.
      if (customFieldValueId === 0 && response?.CustomImageId && photoField)
      {
        try
        {
          await apiService.post('/customfieldvalue/', {
            ...photoField,
            Value: response.CustomImageId,
            binary: serverUrl,
          });
        }
        catch (saveErr)
        {
          console.error('[ProjectHeader] Error saving custom field value for photo:', saveErr);
        }
      }

      setLocalImageUrl(serverUrl);
      conversationBus.emit('image-updated', {
        conversationId: projectInfo.ConversationId,
        imageUrl: serverUrl,
      });
    }
    catch (err)
    {
      console.error('Error uploading image:', err);
      setLocalImageUrl(null);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Body modal (opens as a popover to the right of, and level with, the click point)
  const POPOVER_WIDTH = 520;
  const POPOVER_GAP = 16;
  const POPOVER_MARGIN = 16;

  const openBodyModal = (clickX: number, clickY: number) =>
  {
    setDraftBody(localDescription || '');
    setModalAnchorPoint({ x: clickX, y: clickY });
    setModalPopoverStyle(prev => ({ ...prev, ready: false }));
    setShowBodyModal(true);
    setMenuOpen(false);
  };

  const handleSaveBody = async () =>
  {
    setSavingBody(true);

    // The editor propagates its content debounced; read it directly on save.
    const liveBody = summernoteRef.current?.getContent?.();
    const bodyToSave = typeof liveBody === 'string' ? liveBody : draftBody;

    try
    {
      await dashboardService.updateConversationBody({
        Body: bodyToSave,
        ConversationId: projectInfo.ConversationId,
        ConfigValueId: projectInfo.ConfigValueID,
        SocketConnectionId: socketConnectionId,
      });
      setLocalDescription(bodyToSave);
      conversationBus.emit('description-updated', {
        conversationId: projectInfo.ConversationId,
        description: bodyToSave,
      });
      setShowBodyModal(false);
    }
    catch (err)
    {
      console.error('Error saving body:', err);
    }
    finally
    {
      setSavingBody(false);
    }
  };

  const onEditProjectColumn = () =>
  {
    setMenuOpen(false);
    setConfigProjectColumn(true);
  };

  const onConfigSpByPerson = () =>
  {
    setMenuOpen(false);
    setConfigSpByPerson(true);
  };

  // Dropdown actions
  const handleOpenEditCard = async (duplicateProject: boolean = false) =>
  {
    setMenuOpen(false);

    if (projectInfo && editCardInitRef.current)
    {
      try
      {
        const convData = await ConversationApi.getConversationById(apiConfig, {
          conversationId: projectInfo.ConversationId,
          layersToHide: '',
          getAllReplies: true,
          isConversationDetailsView: false,
          totalReplies: 0,
        });

        // Use the real fetched conversation data, merging some Project values just in case
        editCardInitRef.current({
          ...projectInfo,
          ...convData,
          ConversationId: projectInfo.ConversationId,
          Title: projectInfo.Name,
          Body: convData.Body || projectInfo.Description,
          ProjectId: projectId,
          ConfigValueId: projectInfo.ConfigValueID,
          IsProjectConversation: true,
          ConversationLayers: (projectInfo.ConversationLayers?.length
            ? projectInfo.ConversationLayers
            : (projectInfo.LayerCustomFields || []).map(layer => ({
              LayerId: layer.LayerId,
              LayerName: layer.Name,
              Name: layer.Name,
              LayerColor: layer.Color,
              Color: layer.Color,
              CustomFields: layer.CustomFields || [],
            }))
          ).filter((layer: any) => layer.LayerId !== 0),
          layerCustomFields: projectInfo.LayerCustomFields || [],
          LayerCustomFields: projectInfo.LayerCustomFields || [],
          ConversationGroups: projectInfo.ConversationGroups || [],
          duplicateProject: duplicateProject,
        });
        editCardRef.current?.openModal();
      }
      catch (err)
      {
        console.error('Failed to load conversation for EditCard:', err);
      }
    }
  };

  const handleDuplicateProject = async () =>
  {
    setMenuOpen(false);
    handleOpenEditCard(true);
  };

  // Resolve image source
  const imageSrc = localImageUrl || projectInfo.ProjectImage?.ImagePath || null;

  // Custom fields helpers
  const firstLayer = projectInfo.LayerCustomFields?.[1] || null;
  const allLayers = projectInfo.LayerCustomFields || [];
  return (
    <>
      <div className={`project-header ${isBodyExpanded ? 'project-header--expanded' : ''}`}>
        <div className="project-header__row">
          {/* ─── Image Area ─── */}
          <div
            className="project-header__image"
            onMouseEnter={() => setImageHovered(true)}
            onMouseLeave={() => setImageHovered(false)}
            onClick={handleImageClick}
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={projectInfo.Name}
                className="project-header__image-img"
                loading="lazy"
              />
            ) : (
              <div className="project-header__image-placeholder">
                <Folder size={80} fill="#999999" color="#999999" strokeWidth={0} />
              </div>
            )}

            {/* Hover overlay — always available */}
            <div className={`project-header__image-overlay ${imageHovered ? 'visible' : ''}`}>
              <Camera size={18} />
              <span>Change</span>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="project-header__file-input"
              onChange={handleImageUpload}
            />
          </div>

          {/* ─── Project Info ─── */}
          <div className="project-header__info">
            {/* Title row */}
            <div className="project-header__title-row">
              {isEditing ? (
                <input
                  ref={inputRef}
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  onBlur={saveProjectName}
                  onKeyDown={e =>
                  {
                    if (e.key === 'Enter') saveProjectName();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  className="project-header__title-input"
                />
              ) : (
                <h1
                  className="project-header__title"
                  onClick={() => onOpenInfoWindow(projectInfo.ConversationId)}
                  onDoubleClick={() => setIsEditing(true)}
                  title="Doble clic para editar título"
                >
                  {localName}
                </h1>
              )}
            </div>

            {/* Body (truncated) */}
            <div className={`project-header__body ${isBodyExpanded ? 'project-header__body--expanded' : ''}`}>
              {localDescription && localDescription !== '<p><br></p>' ? (
                <>
                  <div
                    ref={bodyContentRef}
                    className={`project-header__body-content ${isBodyExpanded ? 'project-header__body-content--expanded' : ''}`}
                    onClick={e =>
                    {
                      if ((e.target as HTMLElement).closest('a')) return;
                      openBodyModal(e.clientX, e.clientY);
                    }}
                    dangerouslySetInnerHTML={{ __html: addTargetBlankToLinks(localDescription) }}
                  />
                </>
              ) : (
                <div
                  className="project-header__not-set-link"
                  onClick={e => openBodyModal(e.clientX, e.clientY)}
                >
                  Not set
                </div>
              )}
            </div>
            {localDescription && localDescription !== '<p><br></p>' && isBodyOverflowing &&
              <button className="project-header__see-more" onClick={() => setIsBodyExpanded(prev => !prev)}>
                {isBodyExpanded ? 'See Less' : 'See More'}
              </button>
            }
          </div>

          {/* ─── Custom Fields (preview) ─── */}
          <div className="project-header__fields">
            {firstLayer && firstLayer.CustomFields?.length > 0 && (
              <>
                <div
                  className="project-header__fields-bar"
                  style={{ borderColor: firstLayer.Color || '#ccc' }}
                />
                <div className="project-header__fields-list">
                  {firstLayer.CustomFields.slice(0, 6).map((field: any, index: number) => (
                    <span key={field.CustomFieldId ?? index} className="project-header__field">
                      <div className="project-header__field-label">{field.Name}:</div>
                      <div className="project-header__field-value">
                        {field.SelectedValuesText || field.Value?.trim() || 'Not Set'}
                      </div>
                    </span>
                  ))}
                </div>

                {(firstLayer.CustomFields.length > 6 || allLayers.length > 2) && (
                  <button
                    className="project-header__show-more"
                    onClick={() => onShowMoreDetails?.()}
                  >
                    Show More Details
                  </button>
                )}
              </>
            )}
          </div>
          <div className='flex-1'></div>

          {/* ─── Menu Button ─── */}
          <div className="project-header__menu-wrapper" ref={menuRef}>
            <button className="project-header__menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
              <MoreVertical size={16} />
            </button>

            {menuOpen && (
              <div className="project-header__dropdown">
                <button
                  className="project-header__dropdown-item"
                  onClick={() =>
                  {
                    handleOpenEditCard();
                  }}
                >
                  <i className="icon-pencil-icon"></i>
                  Edit Project Details
                </button>
                <button
                  className="project-header__dropdown-item"
                  onClick={() =>
                  {
                    setMenuOpen(false);
                    onOpenInfoWindow(projectInfo.ConversationId);
                  }}
                >
                  <i
                    className="material-icons ng-scope ng-isolate-scope"
                    data-md-menu-align-target=""
                  >
                    chrome_reader_mode
                  </i>
                  Open Info Window
                </button>
                <button className="project-header__dropdown-item" onClick={handleDuplicateProject}>
                  <i className="material-icons">content_copy</i>
                  Duplicate Project
                </button>
                {CorgeeStorageHelper.canEdit() && (
                  <button
                    className="project-header__dropdown-item"
                    onClick={() => onEditProjectColumn()}
                  >
                    <i id="icon-color-epc" className="fa fa-sliders ng-scope"></i>
                    Edit Project Column
                  </button>
                )}
                <button
                  className="project-header__dropdown-item"
                  onClick={() => onConfigSpByPerson()}
                >
                  <i id="icon-color-epc" className="fa fa-users ng-scope"></i>
                  Config SP By Person
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Body Editor Popover ─── */}
      {showBodyModal && (
        <div className="project-header__modal-backdrop">
          <div
            ref={popoverRef}
            className="project-header__modal"
            style={{
              top: modalPopoverStyle.top,
              left: modalPopoverStyle.left,
              width: POPOVER_WIDTH,
              visibility: modalPopoverStyle.ready ? 'visible' : 'hidden',
              ['--arrow-top' as string]: `${modalPopoverStyle.arrowTop}px`,
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              className="project-header__modal-close"
              onClick={() => setShowBodyModal(false)}
            >
              <X size={15} strokeWidth={4} />
            </button>

            <div className="project-header__modal-body">
              <SummernoteCorgee
                ref={summernoteRef}
                body={draftBody}
                onChangeBody={(content: string) => setDraftBody(content)}
                projectId={projectId}
                configValueId={projectInfo.ConfigValueID}
                textHeight="300px"
                layers={[]}
                conversationId={projectInfo.ConversationId}
                api={apiConfig}
              />
            </div>

            <div className="project-header__modal-footer">
              <button
                className="project-header__modal-save"
                onClick={handleSaveBody}
                disabled={savingBody}
              >
                {savingBody ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── EditCard Modal ─── */}
      <EditCard
        ref={editCardRef}
        config={{
          apiUrl: apiConfig.url,
          authToken: apiConfig.token,
          SocketConnectionId: socketConnectionId,
          attachReactFunctions: (fns: any) =>
          {
            editCardInitRef.current = fns.initConfig;
          },
          functions: {
            conversationResponse: () =>
            {
              // EditCard will handle emitting 'conversation-updated' bus events internally
            },
          },
          params: { Conversation: {} },
        }}
        UpdateCustomFields={() => {}}
      />
      {configProjectColumn && (
        <RenderInBody>
          <EditDashboardColumn
            dashboard={{ ProjectId: projectId } as any}
            projectId={projectId}
            open={configProjectColumn}
            setOpen={setConfigProjectColumn}
            typeConfiguration="Project"
            // reloadDashboard={setReloadDashboard}
          />
        </RenderInBody>
      )}
      {configSpByPerson && (
        <RenderInBody>
          <ConfigSpModal
            projectId={projectId}
            open={configSpByPerson}
            setOpen={setConfigSpByPerson}
          />
        </RenderInBody>
      )}
    </>
  );
};
